import { useCallback } from 'react';
import { apiService } from '../services/api';
import {
  dispatchCarouselGeneration,
  pollCarouselStatus,
} from '../services/carouselGeneration';
import type { CarouselGenerateRequest, ImageOption } from '../types/carousel';
import { useGenerationJobs } from './useGenerationJobs';

/**
 * Job state for the async carousel generation flow. No longer block on the
 * POST response — after dispatch the job is "pending" and this hook polls the
 * status endpoint until done/error/timeout.
 *
 * State is tracked per post (see useGenerationJobs), so several posts can
 * generate at once.
 *
 * imageOptions/selectedImageIndex on 'done' are set only when one slide's
 * quality gate exhausted every attempt — a caveat on an otherwise-done job
 * (see CarouselGenerateResponse), not a distinct status.
 */
export type CarouselGenerationState =
  | { status: 'idle' }
  | { status: 'pending'; postId: string }
  | {
      status: 'done';
      postId: string;
      imageUrls: string[];
      imageOptions?: ImageOption[] | null;
      selectedImageIndex?: number | null;
    }
  | { status: 'error'; postId: string; errorMessage: string }
  | { status: 'timeout'; postId: string };

export interface CarouselGenerationCallbacks {
  onDone?: (
    postId: string,
    imageUrls: string[],
    imageOptions?: ImageOption[] | null,
    selectedImageIndex?: number | null,
    meta?: { alreadyGenerated?: boolean }
  ) => void | Promise<void>;
  onError?: (postId: string, message: string) => void | Promise<void>;
  onTimeout?: (postId: string) => void | Promise<void>;
}

const IDLE: CarouselGenerationState = { status: 'idle' };

export function useCarouselGeneration() {
  const { jobs, setJob, stopPolling, trackPolling, clear } = useGenerationJobs<CarouselGenerationState>();

  // Polls pollPostId's job, recording its state under the editor's postId.
  const poll = useCallback(
    (postId: string, pollPostId: string, callbacks: CarouselGenerationCallbacks) => {
      const stop = pollCarouselStatus(apiService, {
        postId: pollPostId,
        onDone: async (imageUrls, imageOptions, selectedImageIndex) => {
          setJob(postId, { status: 'done', postId: pollPostId, imageUrls, imageOptions, selectedImageIndex });
          await callbacks.onDone?.(pollPostId, imageUrls, imageOptions, selectedImageIndex);
        },
        onError: async (message) => {
          setJob(postId, { status: 'error', postId: pollPostId, errorMessage: message });
          await callbacks.onError?.(pollPostId, message);
        },
        onTimeout: async () => {
          setJob(postId, { status: 'timeout', postId: pollPostId });
          await callbacks.onTimeout?.(pollPostId);
        },
      });
      trackPolling(postId, stop);
    },
    [setJob, trackPolling]
  );

  const start = useCallback(
    async (request: CarouselGenerateRequest, callbacks: CarouselGenerationCallbacks = {}) => {
      const postId = request.post_id;
      // Replaces only this post's previous job — other posts keep generating.
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });

      let dispatched;
      try {
        dispatched = await dispatchCarouselGeneration(apiService, request);
      } catch (err: any) {
        const message = err?.message || 'Could not start generation. Please try again.';
        setJob(postId, { status: 'error', postId, errorMessage: message });
        await callbacks.onError?.(postId, message);
        return;
      }

      // Guard: the post already has a carousel and force_regenerate wasn't set.
      // Use the existing slide URLs instead of re-generating.
      const alreadyGenerated =
        dispatched.response &&
        'error_code' in dispatched.response &&
        dispatched.response.error_code === 'ALREADY_GENERATED';
      if (alreadyGenerated) {
        const existing = dispatched.response as import('../types/carousel').CarouselAlreadyGeneratedResponse;
        const existingUrls =
          existing.output_paths?.length
            ? existing.output_paths
            : existing.output_path
              ? [existing.output_path]
              : [];
        if (existingUrls.length > 0) {
          setJob(postId, { status: 'done', postId, imageUrls: existingUrls });
          await callbacks.onDone?.(postId, existingUrls, null, null, { alreadyGenerated: true });
        } else {
          const message =
            existing.message || 'This post already has a generated carousel.';
          setJob(postId, { status: 'error', postId, errorMessage: message });
          await callbacks.onError?.(postId, message);
        }
        return;
      }

      poll(postId, dispatched.postId || postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const retry = useCallback(
    async (request: CarouselGenerateRequest, callbacks: CarouselGenerationCallbacks = {}) => {
      // Explicit user action — force a fresh generation so a still-running
      // previous job (e.g. the timed-out one) can't trip ALREADY_GENERATED.
      await start({ ...request, force_regenerate: true }, callbacks);
    },
    [start]
  );

  // Resumes polling for a job that's already running server-side (e.g. the
  // user refreshed the page mid-generation) without dispatching a new one.
  const resume = useCallback(
    (postId: string, callbacks: CarouselGenerationCallbacks = {}) => {
      stopPolling(postId);
      setJob(postId, { status: 'pending', postId });
      poll(postId, postId, callbacks);
    },
    [poll, setJob, stopPolling]
  );

  const stateFor = (postId?: string | null): CarouselGenerationState => (postId && jobs[postId]) || IDLE;

  return {
    stateFor,
    isPendingFor: (postId?: string | null) => stateFor(postId).status === 'pending',
    isErrorFor: (postId?: string | null) => stateFor(postId).status === 'error',
    isTimedOutFor: (postId?: string | null) => stateFor(postId).status === 'timeout',
    errorMessageFor: (postId?: string | null) => {
      const state = stateFor(postId);
      return state.status === 'error' ? state.errorMessage : null;
    },
    start,
    retry,
    resume,
    clear,
  };
}
